import React, { useCallback, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Skeleton,
  Tooltip,
  FormHelperText,
  Tabs,
  Tab,
} from '@mui/material';
import styles from './TaxInvoices.module.css';
import InvoiceDetails from './InvoiceDetails';
import TaxInvoiceReceipt from './TaxInvoiceReceipt';
import DisablePopup from './DisablePopup/DisablePopup';
import DoneIcon from '@mui/icons-material/Done';
import { getInstituteById } from '../../../Manage/InstituteProfile/service/instituteSlice';
import { useAuthUser } from 'react-auth-kit';
import { useDispatch } from 'react-redux';
import { resetSettingsStatuses } from '../../service/settingsSlice';
import { getSubDomainAccessStatus } from '../../../../../components/RolePermissionAccess/RolePermissionAccess';
import { useSelector } from 'react-redux';
import AutoCompleteField from '../../../../../components/AutoCompleteField/AutoCompleteField';
import AutoCompleteFilter from '../../../Reports/components/Filters/AutoCompleteFilter';
import AddIcon from '@mui/icons-material/Add';
import { useCountries } from 'use-react-countries';
import SwitchInput from '../../../../../components/Switch/SwitchInput';
import { getBranchList } from '../../../Manage/ManageBranch/service/Branchslice';

const vatRegexByCountry: Record<string, RegExp> = {
  Qatar: /^[0-9]{6}$/,
  'United Arab Emirates': /^[1-9][0-9]{14}$/,
};
const vatPattern = /^[a-zA-Z0-9]{15}$/;

function TaxInvoices(props: any) {
  const {
    GetStudentInvoiceSettings,
    CreateStudentInvoiceSettings,
    UpdateInvoiceFooter,
    UpdateStudentInvoiceSettings,
    invoiceSettingLoading,
    invoiceSettingCreateLoading,
    invoiceSettings,
    invoiceFooterUpdateSuccess,
    invoiceSettingUpdateSuccess,
    invoiceSettingUpdateLoading,
    invoiceSettingCreateSuccess,
  } = props;
  const auth = useAuthUser();
  const authUser = auth();
  const dispatch = useDispatch();
  const { countries } = useCountries();
  const { userById } = useSelector((state: any) => state.userDetails);
  const hasTaxInvoiceAccess = getSubDomainAccessStatus(
    'SETTINGS',
    'INVOICES',
    'ALL_ACCESS',
    userById?.rolePermission
  );
  const hasAllBranchAccess = getSubDomainAccessStatus(
    'GLOBAL_PERMISSIONS',
    'BRANCH_ACCESS',
    'ALL_BRANCHES',
    userById?.rolePermission
  );

  const { branchList } = useSelector((state: any) => state.branch);
  const filteredBranchList = hasAllBranchAccess
    ? branchList
    : branchList?.filter((branch: any) => branch.id === userById?.branchId);

  const [createInvoice, setCreateInvoice] = useState<boolean>(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [gstin, setGstin] = useState('');
  const [vat, setVat] = useState('');
  const [prefix, setPrefix] = useState('');
  const [nextNumber, setNextNumber] = useState('');
  const [showDisablePopup, setShowDisablePopup] = useState(false);

  const [country, setCountry] = React.useState<string>('');
  const [validStateCountry, setValidStateCountry] =
    React.useState<boolean>(true);
  const [selectedBranches, setSelectedBranches] = useState<any>(null);
  const [validStateBranch, setValidStateBranch] = React.useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedInvoice, setSelectedInvoice] = React.useState<any>(null);

  const [isEditingGst, setIsEditingGst] = useState<boolean>(false);
  const [isEditingInvoiceNumber, setIsEditingInvoiceNumber] =
    useState<boolean>(false);
  const [isEditingInvoiceFooter, setIsEditingInvoiceFooter] =
    useState<boolean>(false);

  const gstNumberRegex =
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
  const PREFIX_REGEX = /^[a-zA-Z0-9_]+$/;
  const NUM_REGEX = /^[0-9]*$/;

  const [gstError, setGstError] = React.useState<boolean>(false);
  const [vatError, setVatError] = useState<boolean>(false);
  const [prefixError, setPrefixError] = React.useState<boolean>(false);
  const [numberError, setNumberError] = React.useState<boolean>(false);

  const handleInvoiceTabChange = React.useCallback(
    (event: any, newValue: number) => {
      setActiveTab(newValue);
      setIsEditingInvoiceFooter(false);
      setIsEditingGst(false);
      setIsEditingInvoiceNumber(false);
      const selected = invoiceSettings[newValue];
      setSelectedInvoice(selected);
      setIsEnabled(selected?.isStudentTaxInvoiceEnabled);
      const savedBranches = filteredBranchList?.filter((item: any) =>
        selected?.branchData?.some((branch: any) => branch.branchId === item.id)
      );
      setCountry(selected?.taxInvoiceCountry);
      setSelectedInvoice(selected);
      setPrefix(selected?.prefix);
      setNextNumber(selected?.nextNumber);
      setIsEnabled(selected?.isStudentTaxInvoiceEnabled);
      setSelectedBranches(savedBranches);
      if (selected?.gstNumber) {
        setGstin(selected?.gstNumber);
      } else {
        setVat(selected?.vatNumber);
      }
      const urlParams = new URLSearchParams(window.location.search);
      if (selected?.id) {
        urlParams.set('invoiceId', selected.id);
      } else {
        urlParams.delete('invoiceId');
      }
      urlParams.set('activeTab', String(newValue));

      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.pushState({}, '', newUrl);
    },
    [invoiceSettings]
  );

  const toggleInvoiceFooter = () => {
    setIsEditingInvoiceFooter(!isEditingInvoiceFooter);
  };

  const handleCancelInvoiceFooter = () => {
    setIsEditingInvoiceFooter(false);
  };

  const handleSwitchToggle = (e: any) => {
    const value = e.target.checked;
    setIsEnabled(!isEnabled);

    if (selectedInvoice?.isStudentTaxInvoiceEnabled) {
      setShowDisablePopup(true);
      setIsEditingInvoiceFooter(false);
    }

    if (
      selectedInvoice?.prefix !== '' &&
      !selectedInvoice?.isStudentTaxInvoiceEnabled &&
      value === true &&
      Object?.keys(selectedInvoice)?.length > 0
    ) {
      const branchData =
        selectedBranches?.map((branch: any) => ({
          branchId: branch?.id,
          branchName: branch.branchName,
        })) || [];
      const body = {
        instituteId: authUser?.institute?.[0]?.instituteId,
        nextNumber: selectedInvoice?.nextNumber,
        prefix: selectedInvoice?.prefix,

        ...(selectedInvoice?.gstNumber
          ? { gstNumber: selectedInvoice?.gstNumber }
          : { vatNumber: selectedInvoice?.vatNumber }),
        isStudentTaxInvoiceEnabled: true,
        taxInvoiceCountry: country,
        branchData: branchData,
        id: selectedInvoice?.id,
      };

      UpdateStudentInvoiceSettings(body);
      setIsEditingInvoiceFooter(false);
    }
  };

  const handleNoSwitchToggle = () => {
    setIsEnabled(selectedInvoice?.isStudentTaxInvoiceEnabled);
    setShowDisablePopup(false);
  };

  const handleYesSwitchToggle = () => {
    if (selectedInvoice?.isStudentTaxInvoiceEnabled) {
      setShowDisablePopup(false);
    }
  };

  console.log('gstin', gstin);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = event.target;
      const trimmedValue = value.toUpperCase();

      if (id === 'gstNumber') {
        setGstin(trimmedValue);
        setGstError(
          trimmedValue.length > 0 && !gstNumberRegex.test(trimmedValue)
        );
      } else if (id === 'vatNumber') {
        setVat(trimmedValue);
        const selectedCountry = country;
        const vatRegex = vatRegexByCountry[selectedCountry] || /.*/;

        setVatError(trimmedValue.length > 0 && !vatPattern.test(trimmedValue));
      }
    },
    [country]
  );

  const handleNextClick = () => {
    (country === 'India' ? gstin && !gstError : vat && !vatError) &&
      country !== '' &&
      selectedBranches?.length > 0 &&
      setCurrentStep(currentStep + 1);
  };

  const handlePrefixChange = (e: any) => {
    const inputValue = e.target.value;
    if (PREFIX_REGEX.test(inputValue) || inputValue === '') {
      if (!inputValue) {
        setPrefixError(true);
      } else {
        setPrefixError(false);
      }
      setPrefix(inputValue);
    }
  };

  const handleNumberChange = (e: any) => {
    const inputValue = e.target.value;

    if (NUM_REGEX.test(inputValue) || inputValue === '') {
      if (!inputValue) {
        setNumberError(true);
      } else {
        setNumberError(false);
      }
      setNextNumber(inputValue);
    }
  };

  const handleKeyDown = (event: any) => {
    if (event.keyCode === 8) {
      return;
    }
    if (!NUM_REGEX.test(event.key)) {
      event.preventDefault();
    }
  };

  const onBranchChange = React.useCallback(
    (event: object, value: any | null) => {
      if (value && value?.hasOwnProperty('isAddButton')) {
      } else {
        if (value !== null) {
          setSelectedBranches(value);
          setValidStateBranch(true);
        } else {
          setSelectedBranches(null);
          setValidStateBranch(false);
        }
      }
    },
    []
  );

  const handleSaveClick = async (event: any) => {
    event.preventDefault();

    try {
      const branchData =
        selectedBranches?.map((branch: any) => ({
          branchId: branch?.id,
          branchName: branch.branchName,
        })) || [];
      const body = {
        instituteId: authUser?.institute?.[0]?.instituteId,
        nextNumber: nextNumber,
        prefix: prefix,
        // ...(gstin ? { gstNumber: gstin } : { vatNumber: vat }),
        gstNumber: country === 'India' ? gstin : '',
        vatNumber: country !== 'India' ? vat : '',
        isStudentTaxInvoiceEnabled: true,
        taxInvoiceCountry: country,
        branchData: branchData,
      };

      CreateStudentInvoiceSettings(body);
    } catch (error) {}
  };

  const handleYesToggle = async (event: any) => {
    event.preventDefault();

    try {
      const branchData =
        selectedBranches?.map((branch: any) => ({
          branchId: branch?.id,
          branchName: branch.branchName,
        })) || [];
      const body = {
        instituteId: authUser?.institute?.[0]?.instituteId,
        nextNumber: selectedInvoice?.nextNumber,
        prefix: selectedInvoice?.prefix,

        ...(selectedInvoice?.gstNumber
          ? { gstNumber: selectedInvoice?.gstNumber }
          : { vatNumber: selectedInvoice?.vatNumber }),
        isStudentTaxInvoiceEnabled: false,
        taxInvoiceCountry: country,
        branchData: branchData,
        id: selectedInvoice?.id,
      };
      UpdateStudentInvoiceSettings(body);
    } catch (error) {}
  };

  const toggleShowAddInvoice = () => {
    setCreateInvoice(!createInvoice);
    setCurrentStep(1);
    setPrefix('');
    setNextNumber('');
    setIsEnabled(false);
    setGstin('');
    setVat('');
    setCountry('');
  };

  const blurrEffect = createInvoice
    ? true
    : selectedInvoice?.prefix !== '' &&
      !selectedInvoice?.isStudentTaxInvoiceEnabled
    ? true
    : false;

  React.useEffect(() => {
    if (authUser)
      GetStudentInvoiceSettings(authUser?.institute?.[0]?.instituteId);
  }, []);

  React.useEffect(() => {
    if (invoiceSettings?.length === 0) {
      setCreateInvoice(true);
    } else {
      setCreateInvoice(false);
    }
  }, [invoiceSettings]);

  React.useEffect(() => {
    if (invoiceSettings?.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const invoiceId = urlParams.get('invoiceId');
      const tab = urlParams.get('activeTab');
      if (tab) {
        setActiveTab(Number(tab));
      }
      const selected =
        invoiceSettings?.find((inv: any) => inv.id === invoiceId) ||
        invoiceSettings[0];
      const savedBranches = filteredBranchList?.filter((item: any) =>
        selected?.branchData?.some((branch: any) => branch.branchId === item.id)
      );
      setCountry(selected?.taxInvoiceCountry);
      setSelectedInvoice(selected);
      setPrefix(selected?.prefix);
      setNextNumber(selected?.nextNumber);
      setIsEnabled(selected?.isStudentTaxInvoiceEnabled);
      setSelectedBranches(savedBranches);
      if (selected?.gstNumber) {
        setGstin(selected?.gstNumber);
      } else {
        setVat(selected?.vatNumber);
      }
    }
  }, [invoiceSettings, branchList]);

  React.useEffect(() => {
    setIsEditingInvoiceFooter(false);
    dispatch<any>(getInstituteById(authUser?.institute?.[0]?.instituteId));
    GetStudentInvoiceSettings(authUser?.institute?.[0]?.instituteId);
    dispatch<any>(getBranchList(authUser?.institute?.[0]?.instituteId));
  }, [invoiceFooterUpdateSuccess]);

  React.useEffect(() => {
    if (
      invoiceSettingUpdateSuccess !== true &&
      invoiceSettingUpdateSuccess !== false
    ) {
      setShowDisablePopup(false);
      GetStudentInvoiceSettings(authUser?.institute?.[0]?.instituteId);
      dispatch<any>(getInstituteById(authUser?.institute?.[0]?.instituteId));
      dispatch<any>(getBranchList(authUser?.institute?.[0]?.instituteId));
      dispatch(resetSettingsStatuses());
    }
  }, [invoiceSettingUpdateSuccess]);
  React.useEffect(() => {
    if (
      invoiceSettingCreateSuccess !== true &&
      invoiceSettingCreateSuccess !== false
    ) {
      const params = new URLSearchParams(window.location.search);
      params.delete('invoiceId');
      params.delete('activeTab');

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState({}, '', newUrl);
      setActiveTab(0);
      setShowDisablePopup(false);
      GetStudentInvoiceSettings(authUser?.institute?.[0]?.instituteId);
      dispatch<any>(getInstituteById(authUser?.institute?.[0]?.instituteId));
      dispatch<any>(getBranchList(authUser?.institute?.[0]?.instituteId));
      dispatch(resetSettingsStatuses());
    }
  }, [invoiceSettingCreateSuccess]);

  const countrySelected = countries
    ? countries?.find(
        (item: any) => item.name?.toLowerCase() === country?.toLowerCase()
      )
    : '';
  const onCountryChange = React.useCallback(
    (event: object, value: any | null) => {
      if (value !== null) {
        setCountry(value?.name);
        setValidStateCountry(true);
      } else {
        setCountry('');
        setValidStateCountry(false);
      }
      setGstin('');
      setVat('');
      setSelectedBranches(null);
      setGstError(false);
      setVatError(false);
    },
    []
  );

  const filteredBranches = country
    ? filteredBranchList?.filter((branch: any) => branch.country === country)
    : [];

  return (
    <Box sx={{ padding: ' 24px 20px 20px 20px' }}>
      <div className={styles.pageTitle}>Invoices</div>
      {!invoiceSettingLoading &&
        invoiceSettings?.length > 0 &&
        !createInvoice && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '10px',
              margin: '10px 0px 10px 0px',
              borderBottom: '1px solid #E9E9E9',
              alignItems: 'center',
            }}
          >
            <div>
              <Tabs value={activeTab} onChange={handleInvoiceTabChange}>
                {invoiceSettings?.map((invoice: any, index: number) => {
                  const branches = invoice?.branchData || [];
                  const firstBranchName =
                    branches?.[0]?.branchName || 'Unknown';
                  const remainingCount =
                    branches.length > 1 ? ` +${branches.length - 1}` : '';

                  return (
                    <Tab
                      key={index}
                      label={`${firstBranchName}${remainingCount}`}
                      sx={{
                        textTransform: 'capitalize',
                        color: '#B4B4B4',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '24px',
                        paddingLeft: '0',
                        paddingRight: '0',
                        marginLeft: index > 0 ? '20px' : '0',
                      }}
                    />
                  );
                })}
              </Tabs>
            </div>

            {!createInvoice && hasTaxInvoiceAccess && (
              <div>
                <Button
                  onClick={toggleShowAddInvoice}
                  color="primary"
                  variant="outlined"
                  sx={{
                    textTransform: 'capitalize',
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#667085',
                    border: '1px solid #E6E6E6',
                    borderRadius: '4px',
                  }}
                >
                  <AddIcon sx={{ marginRight: '6px', fontSize: '18px' }} />
                  Add New
                </Button>
              </div>
            )}
          </div>
        )}

      {!invoiceSettingLoading && invoiceSettings !== null ? (
        <>
          {!createInvoice && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',

                borderRadius: '4px',
                border: '1px solid rgba(119, 129, 126, 0.07)',
                background: 'rgba(178, 178, 178, 0.06)',
                padding: '12px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <div className={styles.enableText}>
                  Want to send Tax Invoices to students?
                </div>

                <div style={{ marginLeft: '15px' }}>
                  <SwitchInput
                    switchStatus={isEnabled}
                    handleSwitch={handleSwitchToggle}
                    disabled={hasTaxInvoiceAccess === false}
                    id={'status'}
                    label={'Enable Now'}
                    labelPlacement={'start'}
                    tooltipTitle={
                      hasTaxInvoiceAccess === false
                        ? 'Oops! No permission.'
                        : ''
                    }
                  />
                </div>
              </Box>
            </Box>
          )}
          {/* {(isEnabled || blurrEffect) && ( */}

          {
            <Box className={styles.taxInvoiceDetailsWrapper}>
              <Box
                className={styles.invoiceDetailsBox}
                style={{
                  maxHeight: createInvoice
                    ? 'calc(100vh - 190px)'
                    : 'calc(100vh - 320px)',
                }}
              >
                {createInvoice ? (
                  <Box>
                    <Box className={styles.setupInvoiceHead}>Setup Invoice</Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <div
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            border: '1px solid #11C89B',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#11C89B',
                            fontFamily: 'Poppins',
                            fontSize: '14px',
                            fontWeight: '500',
                            lineHeight: '24px',
                            // border:
                            //   currentStep === 2
                            //     ? "1px solid #11C89B"
                            //     : "1px solid #D9D9D9",
                          }}
                        >
                          {/* {currentStep === 1 ? "1" : <DoneIcon />} */}1
                        </div>
                        <div className={styles.step}>Step 1</div>
                      </Box>
                      <div
                        style={{
                          color: '#E9E9E9',
                          width: '57px',
                          height: '1px',
                          background: '#E9E9E9',
                        }}
                      ></div>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <div
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: 'Poppins',
                            fontSize: '14px',
                            fontWeight: '500',
                            lineHeight: '24px',
                            border:
                              currentStep === 2
                                ? '1px solid #11C89B'
                                : '1px solid #D9D9D9',
                            color: currentStep === 2 ? '#11C89B' : ' #667085',
                          }}
                        >
                          2
                        </div>
                        <div className={styles.step}>Step 2</div>
                      </Box>
                    </Box>
                    {currentStep === 1 && (
                      <React.Fragment>
                        <div style={{ margin: '15px 0' }}>
                          <AutoCompleteField
                            id="country"
                            label="Select Country"
                            options={countries?.filter((c: any) =>
                              filteredBranchList?.some(
                                (b: any) => b.country === c.name
                              )
                            )}
                            optionLabel={'name'}
                            onChange={onCountryChange}
                            value={countrySelected}
                            disableClearable={country === '' ? true : false}
                            required={true}
                            validState={validStateCountry}
                          />
                        </div>
                        {country !== '' && (
                          <>
                            <div className={styles.gstIn}>
                              {country === 'India' ? 'GSTIN' : 'VAT'}

                              <span
                                style={{ color: '#EC3939', fontWeight: '400' }}
                              >
                                *
                              </span>
                            </div>
                            {country === 'India' ? (
                              <TextField
                                id="gstNumber"
                                variant="outlined"
                                fullWidth
                                value={gstin}
                                inputProps={{ maxLength: 15 }}
                                error={gstError}
                                helperText={
                                  gstError ? 'Invalid GST Number' : ''
                                }
                                onChange={handleChange}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-input': {
                                    padding: '11px',
                                  },
                                }}
                              />
                            ) : (
                              <TextField
                                id="vatNumber"
                                variant="outlined"
                                fullWidth
                                value={vat}
                                inputProps={{ maxLength: 15 }}
                                error={vatError}
                                helperText={
                                  vatError ? 'Invalid VAT Number' : ''
                                }
                                onChange={handleChange}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-input': {
                                    padding: '11px',
                                  },
                                }}
                              />
                            )}

                            <div style={{ margin: '15px 0' }}>
                              <AutoCompleteFilter
                                id="branchId"
                                label="Select Branch"
                                options={filteredBranches}
                                optionLabel={'branchName'}
                                onChange={onBranchChange}
                                value={selectedBranches}
                                disableClearable={
                                  selectedBranches === null ? true : false
                                }
                                validState={true}
                                multiSelect={true}
                                width={'100%'}
                                listType="BRANCH"
                                required={true}
                              />
                              {!validStateBranch && (
                                <FormHelperText
                                  sx={{ marginLeft: '8px' }}
                                  error
                                >
                                  Field required
                                </FormHelperText>
                              )}
                            </div>
                          </>
                        )}
                      </React.Fragment>
                    )}
                    {currentStep === 2 && (
                      <React.Fragment>
                        <div className={styles.invoiceSectionHead}>
                          Customize your Invoice number by setting a prefix and
                          starting number.
                        </div>
                        <div className={styles.invoiceSectionSubHead}>
                          The system will generate them sequentially, ensuring a
                          unique identification for each invoice.
                        </div>
                        <Box sx={{ display: 'flex', maxWidth: '86%' }}>
                          <Box>
                            <div className={styles.prefix}>
                              Prefix{' '}
                              <span
                                style={{ color: '#EC3939', fontWeight: '400' }}
                              >
                                *
                              </span>
                            </div>
                            <TextField
                              value={prefix}
                              onChange={handlePrefixChange}
                              onKeyDown={(event: any) => {
                                if (!PREFIX_REGEX.test(event.key)) {
                                  event.preventDefault();
                                }
                              }}
                              sx={{
                                width: '72%',
                                '& .MuiOutlinedInput-input': {
                                  padding: '11px',
                                },
                              }}
                              inputProps={{ maxLength: 10 }}
                              error={prefixError}
                              helperText={prefixError ? 'Field Required' : ''}
                            />
                          </Box>
                          <Box>
                            <div className={styles.nextNumber}>
                              Next Number{' '}
                              <span
                                style={{ color: '#EC3939', fontWeight: '400' }}
                              >
                                *
                              </span>
                            </div>
                            <TextField
                              value={nextNumber}
                              onChange={handleNumberChange}
                              sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-input': {
                                  padding: '11px',
                                },
                              }}
                              inputProps={{ maxLength: 10 }}
                              error={numberError}
                              helperText={numberError ? 'Field Required' : ''}
                              onKeyDown={handleKeyDown}
                            />
                          </Box>
                        </Box>
                      </React.Fragment>
                    )}

                    <Box
                      sx={{
                        marginTop: '24px',
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {((currentStep === 1 && invoiceSettings?.length > 0) ||
                        currentStep === 2) && (
                        <Button
                          sx={{
                            color: '#BFBFBF',
                            border: '1px solid #BFBFBF',
                            background: '#FFF',
                            textTransform: 'capitalize',
                          }}
                          variant="outlined"
                          onClick={() => {
                            if (
                              invoiceSettings?.length === 0 &&
                              currentStep === 2
                            ) {
                              setCurrentStep(1);
                              setPrefix('');
                              setNextNumber('');
                            } else {
                              setCreateInvoice(false);
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      )}

                      <Tooltip
                        arrow
                        title={
                          hasTaxInvoiceAccess === false
                            ? 'Oops! No permission.'
                            : ''
                        }
                        PopperProps={{ style: { zIndex: 9999999 } }}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: '#101828',
                              color: '#fff',
                              fontSize: '12px',
                              fontWeight: '500',
                              padding: '8px 12px',
                              zIndex: '9999999',
                              '& .MuiTooltip-arrow': {
                                color: '#101828',
                                zIndex: '9999999',
                              },
                            },
                          },
                        }}
                      >
                        <span>
                          <Button
                            sx={{ textTransform: 'capitalize' }}
                            variant="contained"
                            disabled={
                              (currentStep === 1
                                ? (country === 'India' ? !gstin : !vat) ||
                                  country === '' ||
                                  selectedBranches === null
                                : !prefix ||
                                  !nextNumber ||
                                  invoiceSettingCreateLoading) ||
                              hasTaxInvoiceAccess === false
                            }
                            onClick={
                              currentStep === 1
                                ? handleNextClick
                                : handleSaveClick
                            }
                          >
                            {invoiceSettingCreateLoading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : currentStep === 1 ? (
                              'Next'
                            ) : (
                              'Save'
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                ) : (
                  <InvoiceDetails
                    disabled={handleYesSwitchToggle}
                    UpdateInvoiceFooter={UpdateInvoiceFooter}
                    UpdateStudentInvoiceSettings={UpdateStudentInvoiceSettings}
                    invoiceSettingUpdateSuccess={invoiceSettingUpdateSuccess}
                    // invoiceSettings={invoiceSettings}
                    invoiceSettings={selectedInvoice}
                    blurrEffect={blurrEffect}
                    hasTaxInvoiceAccess={hasTaxInvoiceAccess}
                    selectedCountry={country}
                    invoiceFooter={selectedInvoice?.invoiceFooter}
                    handleCancelInvoiceFooter={handleCancelInvoiceFooter}
                    isEditingInvoiceFooter={isEditingInvoiceFooter}
                    toggleInvoiceFooter={toggleInvoiceFooter}
                    setCreateInvoice={setCreateInvoice}
                    isEditingGst={isEditingGst}
                    setIsEditingGst={setIsEditingGst}
                    isEditingInvoiceNumber={isEditingInvoiceNumber}
                    setIsEditingInvoiceNumber={setIsEditingInvoiceNumber}
                  />
                )}
              </Box>

              <Box
                className={styles.invoicePreviewWrapper}
                style={{
                  maxHeight: createInvoice
                    ? 'calc(100vh - 190px)'
                    : 'calc(100vh - 320px)',
                }}
              >
                <div
                  className={styles.invoicePreview}
                  style={{ color: blurrEffect ? '#C5C5C5' : '#667085' }}
                >
                  Invoice Preview
                </div>
                <TaxInvoiceReceipt
                  selectedInvoice={selectedInvoice}
                  blurrEffect={blurrEffect}
                  selectedCountry={country}
                />
              </Box>
            </Box>
          }
        </>
      ) : (
        Array.from(new Array(1)).map((_, index) => (
          <div key={index} style={{ marginBottom: '8px' }}>
            <div className={styles.leadCourseSkeletonWrap}>
              <Skeleton animation="wave" variant="rounded" height={60} />
            </div>
          </div>
        ))
      )}

      {showDisablePopup && (
        <DisablePopup
          handleYesSwitchToggle={handleYesToggle}
          onClose={handleNoSwitchToggle}
          showDisablePopup={showDisablePopup}
          loading={invoiceSettingUpdateLoading}
        />
      )}
    </Box>
  );
}

export default TaxInvoices;
